// The `{ data, next_cursor }` envelope as a SCHEMA — the runtime half of
// `paged-types.ts` (plan 101 slice 15 step 3).
//
// `Page<T>` states the envelope as a TYPE, and a type is a claim nothing runs:
// `apiFetch<Page<T>>` compiled exactly as happily on the day the server stopped
// sending a field, because the door ends in a cast. Parsing is what turns that
// into an answer, and every parsed collection needs the same two keys wrapped
// around its row schema.
//
// It lives here rather than per endpoint because the FIRST parsed collection
// (`/v1/runners`) declared a local `runnersPageSchema` and the second would have
// been the second copy — `scripts/one-impl.test.ts`'s rule, applied to a shape.

import { z } from 'zod';

/**
 * The canonical list slice over `row`.
 *
 * NON-STRICT by construction — a plain `z.object` ignores unknown keys — which
 * is the property `parseBody` (`lib/api-errors.ts`) requires of every schema
 * handed to `apiFetch`: a `/v1` collection gaining a field is an ordinary
 * additive change and must never break a bundle already sitting in a browser.
 * Only the other direction throws: a field that vanished, changed type or went
 * null, which is the drift a mirror could never see.
 */
export function pageSchema<S extends z.ZodType>(
  row: S,
): z.ZodObject<{ data: z.ZodArray<S>; next_cursor: z.ZodNullable<z.ZodString> }> {
  return z.object({ data: z.array(row), next_cursor: z.string().nullable() });
}
